import { ApiResponse } from '../models/api/api-response.model';
import { AppJsonDecoder } from '../utils/app-json-decoder';
import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, tap } from 'rxjs';
import { API } from '../constants/api';

export class DashboardSectionsState {
  retrieved: boolean;
  genPassFunc: boolean;
  featuresAdminSection: boolean;
  classesAdminSection: boolean;
  techAdminSection: boolean;
  filmsAdminSection: boolean;
  licensesEditor: boolean;
  licensesAdmin: boolean;
  ptSection: boolean;
  ptAdminSection: boolean;
  managementSection: boolean;
  agreementsSection: boolean;
  emsAdmin: boolean;
  ems: boolean;

  constructor(json?: any) {
    json ??= {};
    this.retrieved = AppJsonDecoder.optBool(json.retrieved) || false;
    this.genPassFunc = AppJsonDecoder.optBool(json.genPassFunc) || false;
    this.featuresAdminSection =
      AppJsonDecoder.optBool(json.featuresAdminSection) || false;
    this.classesAdminSection =
      AppJsonDecoder.optBool(json.classesAdminSection) || false;
    this.techAdminSection =
      AppJsonDecoder.optBool(json.techAdminSection) || false;
    this.filmsAdminSection =
      AppJsonDecoder.optBool(json.filmsAdminSection) || false;
    this.licensesEditor = AppJsonDecoder.optBool(json.licensesEditor) || false;
    this.licensesAdmin = AppJsonDecoder.optBool(json.licensesAdmin) || false;
    this.ptSection = AppJsonDecoder.optBool(json.ptSection) || false;
    this.ptAdminSection = AppJsonDecoder.optBool(json.ptAdminSection) || false;
    this.managementSection =
      AppJsonDecoder.optBool(json.managementSection) || false;
    this.agreementsSection =
      AppJsonDecoder.optBool(json.agreementsSection) || false;
    this.emsAdmin = AppJsonDecoder.optBool(json.emsAdmin) || false;
    this.ems = AppJsonDecoder.optBool(json.ems) || false;
  }
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private _http: HttpClient = inject(HttpClient);
  private _sectionsState$ = new BehaviorSubject<DashboardSectionsState>(
    new DashboardSectionsState(),
  );

  get sectionsState$(): BehaviorSubject<DashboardSectionsState> {
    return this._sectionsState$;
  }

  login(username: string, password: string) {
    return this._http
      .post<ApiResponse<string>>(
        API.login(),
        { username: username, password: password },
        {
          withCredentials: true,
          responseType: 'json',
          headers: {
            Audience: 'Dashboard',
            'X-Auth-Mode': 'cookie',
            'Content-Type': 'application/json',
          },
        },
      )
      .pipe(tap(() => this.initSections()));
  }

  logout() {
    return this._http
      .get(API.logout(), {
        withCredentials: true,
        responseType: 'json',
        headers: {
          'X-Auth-Mode': 'cookie',
          'Content-Type': 'application/json',
        },
      })
      .pipe(tap(() => this._sectionsState$.next(new DashboardSectionsState())));
  }

  token() {
    return this._http.get(API.token(), {
      withCredentials: true,
      responseType: 'json',
      headers: {
        Audience: 'Dashboard',
        'X-Auth-Mode': 'cookie',
        'Content-Type': 'application/json',
      },
    });
  }

  session() {
    return this._http.get(API.session(), {
      withCredentials: true,
      responseType: 'json',
      headers: {
        'X-Auth-Mode': 'cookie',
        'Content-Type': 'application/json',
      },
    });
  }

  tmpCredentials(email?: string, withCredentials?: boolean) {
    withCredentials ??= false;
    return this._http.get<ApiResponse<any>>(API.tmpCred(email), {
      withCredentials: withCredentials,
      responseType: 'json',
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  credentials(
    email: string,
    tmp_password: string,
    password: string,
    verify_password: string,
  ) {
    return this._http.post(
      API.credentials(),
      { email, tmp_password, password, verify_password },
      {
        withCredentials: true,
        responseType: 'json',
        headers: {
          Audience: 'Dashboard',
          'X-Auth-Mode': 'cookie',
          'Content-Type': 'application/json',
        },
      },
    );
  }

  // Sections Authorization
  getSectionsAuth() {
    return this._http.get(API.dashboardSections(), {
      withCredentials: true,
    });
  }

  async initSections() {
    if (this._sectionsState$.getValue().retrieved === false) {
      this.getSectionsAuth().subscribe({
        next: (data: any) => {
          this._sectionsState$.next(
            new DashboardSectionsState(
              Object.assign(data.data, { retrieved: true }),
            ),
          );
        },
        error: (error) => {
          console.error('initSections | Error:', error);
          this._sectionsState$.next(new DashboardSectionsState());
        },
      });
    }
  }
}
